import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Download, Loader2, XCircle, Printer, MapPin, Phone } from 'lucide-react';
import { paymentAPI, bookingAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';

// Helper to parse date strings, converting from UTC to Australian time if needed
const parseDateString = (dateStr) => {
  if (!dateStr) return new Date();
  
  // If it's a simple YYYY-MM-DD format (new bookings), parse as local date
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // For ISO strings with time (old bookings), convert from UTC to Australian time
  if (dateStr.includes('T')) {
    // Parse as UTC and add 11 hours for Australian Eastern Time
    const utcDate = new Date(dateStr);
    // Add 11 hours to convert UTC to AEDT
    utcDate.setHours(utcDate.getHours() + 11);
    return utcDate;
  }
  
  return new Date(dateStr);
};

const PaymentSuccessPage = () => {
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      setPaymentStatus('cancelled');
      return;
    }

    const pollPaymentStatus = async (attemptCount = 0) => {
      if (attemptCount >= maxAttempts) {
        setPaymentStatus('cancelled');
        toast({
          title: "Payment Verification Failed",
          description: "Unable to verify payment status.",
          variant: "destructive"
        });
        return;
      }

      try {
        const status = await paymentAPI.getCheckoutStatus(sessionId);
        
        if (status.payment_status === 'paid' && status.booking_created) {
          const booking = await bookingAPI.getBooking(status.booking_id);
          setBookingDetails(booking);
          setBookingId(status.booking_id);
          setPaymentStatus('success');
          
          toast({
            title: "Payment Successful!",
            description: "Your booking has been confirmed.",
          });
        } else if (status.status === 'expired' || status.payment_status === 'unpaid') {
          setPaymentStatus('cancelled');
        } else {
          setAttempts(attemptCount + 1);
          setTimeout(() => pollPaymentStatus(attemptCount + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attemptCount < maxAttempts - 1) {
          setAttempts(attemptCount + 1);
          setTimeout(() => pollPaymentStatus(attemptCount + 1), 2000);
        } else {
          setPaymentStatus('cancelled');
        }
      }
    };

    pollPaymentStatus();
  }, [toast]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await bookingAPI.downloadPDF(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking_confirmation_${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "PDF Downloaded",
        description: "Your booking confirmation has been downloaded.",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  // Calculate duration
  const calculateDuration = () => {
    if (!bookingDetails) return 0;
    const entry = parseDateString(bookingDetails.entryDate);
    const exit = parseDateString(bookingDetails.exitDate);
    return Math.ceil((exit - entry) / (1000 * 60 * 60 * 24));
  };

  // Calculate transfer service price from booking data
  const getTransferPrice = () => {
    if (!bookingDetails) return 0;
    // If stored in booking, use it
    if (bookingDetails.transferServicePrice !== undefined && bookingDetails.transferServicePrice !== null) {
      return bookingDetails.transferServicePrice;
    }
    // For old bookings without transferServicePrice, calculate from totals
    if (bookingDetails.transferService && bookingDetails.parkingSubtotal && bookingDetails.gst) {
      // total = parkingSubtotal + gst, where parkingSubtotal = parking + transfer
      // We can calculate: transfer = total / 1.1 - (parking calculated from daily rate)
      // But daily rates might have changed, so let's use a different approach
      // The subtotal before GST minus parking cost = transfer cost
      // However, we don't know what rate was used. Best approach: store it going forward
      // For legacy bookings, try to derive from the numbers
      const totalBeforeGst = bookingDetails.total / 1.1;
      // If the booking has a very small total (like $2.20), the transfer was probably $1
      // This is a rough estimate for legacy bookings
      if (bookingDetails.total < 50) {
        // For small totals, calculate: subtotal = parking + transfer, so transfer = subtotal - parking_portion
        // Assume equal split or use the actual parkingSubtotal
        const parkingPortion = bookingDetails.parkingSubtotal / 2; // rough estimate
        return bookingDetails.parkingSubtotal - parkingPortion;
      }
      return 200; // Default fallback for larger amounts
    }
    return 0;
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4" 
      style={{ background: 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(179, 229, 252) 50%, rgb(129, 212, 250) 100%)' }}
    >
      <div className="max-w-2xl w-full">
        {paymentStatus === 'checking' && (
          <Card className="border-none shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center">
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#00897B] animate-spin" />
                <h2 className="text-2xl font-bold mb-2 text-gray-800">Processing Payment...</h2>
                <p className="text-gray-600 mb-4">Please wait while we confirm your payment</p>
                <p className="text-sm text-gray-500">Verifying... ({attempts + 1}/{maxAttempts})</p>
              </div>
            </CardContent>
          </Card>
        )}

        {paymentStatus === 'success' && bookingDetails && (
          <Card className="border-none shadow-2xl print:shadow-none">
            <CardContent className="p-8">
              {/* Header */}
              <div className="text-center mb-6 print:mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center print:hidden">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold mb-2 text-[#004280]">BOOKING CONFIRMATION</h1>
                <p className="text-gray-600">Payment Successful - Thank You!</p>
              </div>

              {/* Booking Information */}
              <div className="bg-[#004280] text-white px-4 py-2 rounded-t-lg font-semibold mb-0">
                BOOKING INFORMATION
              </div>
              <div className="bg-gray-50 p-4 rounded-b-lg mb-4 border border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Booking ID</p>
                    <p className="font-semibold text-[#004280]">{bookingDetails.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Booked on</p>
                    <p className="font-semibold">{format(parseDateString(bookingDetails.bookingDate), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer</p>
                    <p className="font-semibold">{bookingDetails.fullName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment</p>
                    <p className="font-bold text-green-600">PAID</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{bookingDetails.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold">{bookingDetails.phone}</p>
                  </div>
                </div>
              </div>

              {/* Parking Details */}
              <div className="bg-[#004280] text-white px-4 py-2 rounded-t-lg font-semibold mb-0">
                PARKING DETAILS
              </div>
              <div className="bg-gray-50 p-4 rounded-b-lg mb-4 border border-gray-200">
                <div className="mb-3">
                  <p className="text-sm text-gray-600">Location Selected</p>
                  <p className="font-semibold text-[#004280]">
                    {bookingDetails.location} | {bookingDetails.location === 'Turrella' 
                      ? '76 Henderson Street, Turrella NSW 2205' 
                      : '100 Marrickville Road, Marrickville NSW 2204'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Entry Date</p>
                    <p className="font-semibold">{format(parseDateString(bookingDetails.entryDate), 'dd MMMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Exit Date</p>
                    <p className="font-semibold">{format(parseDateString(bookingDetails.exitDate), 'dd MMMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold">{calculateDuration()} days</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cars</p>
                    <p className="font-semibold">{bookingDetails.numberOfCars} vehicle(s)</p>
                  </div>
                </div>
              </div>

              {/* Vehicle Registration */}
              <div className="bg-[#004280] text-white px-4 py-2 rounded-t-lg font-semibold mb-0">
                VEHICLE REGISTRATION
              </div>
              <div className="bg-gray-50 p-4 rounded-b-lg mb-4 border border-gray-200">
                {bookingDetails.carRegistrations?.map((reg, idx) => (
                  <p key={idx} className="font-semibold">Car {idx + 1}: {reg}</p>
                ))}
              </div>

              {/* Payment Summary */}
              <div className="bg-[#004280] text-white px-4 py-2 rounded-t-lg font-semibold mb-0">
                PAYMENT SUMMARY
              </div>
              <div className="bg-gray-100 p-4 rounded-b-lg mb-4 border border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      Parking: ${bookingDetails.location === 'Turrella' ? '20' : '30'}/day × {calculateDuration()} days × {bookingDetails.numberOfCars} car(s)
                    </span>
                    <span className="font-semibold">
                      ${(bookingDetails.parkingSubtotal - (bookingDetails.transferService ? getTransferPrice() : 0)).toFixed(2)}
                    </span>
                  </div>
                  {bookingDetails.transferService && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Transfer Service</span>
                      <span className="font-semibold">${getTransferPrice().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-700">GST (10%)</span>
                    <span className="font-semibold">${bookingDetails.gst?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-[#004280] pt-2 mt-2">
                    <span className="text-lg font-bold text-gray-800">TOTAL PAID</span>
                    <span className="text-xl font-bold text-[#004280]">${bookingDetails.total?.toFixed(2)} AUD</span>
                  </div>
                </div>
              </div>

              {/* Location-specific Instructions */}
              {bookingDetails.location === 'Turrella' ? (
                /* Turrella - Directions and Gate Access Instructions */
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-[#004280] font-bold text-lg mb-2">
                    <MapPin className="w-5 h-5" />
                    <span>DIRECTIONS</span>
                  </div>
                  <div className="bg-blue-50 border-2 border-[#004280] rounded-lg p-4 mb-4">
                    <p className="text-gray-700 mb-3">
                      Drive down Henderson Street, it's the last driveway on the left. You will see the sign CP on the double gate outside.
                    </p>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_cruisedock/artifacts/b4iy2gys_location.png" 
                      alt="Turrella parking location map" 
                      className="w-full max-w-md mx-auto rounded-lg border border-gray-300"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-amber-600 font-bold text-lg mb-2">
                    <span>🔒</span>
                    <span>GATE ACCESS INSTRUCTIONS - CODE: {bookingDetails.gateCode || '3175'}</span>
                  </div>
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                    <p className="font-semibold mb-3">To access the parking area:</p>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Locate the gate with the combination lock</li>
                      <li>Enter the code <span className="font-bold text-amber-600">{bookingDetails.gateCode || '3175'}</span> on the engraved line</li>
                      <li>Unlock the gate</li>
                      <li>Drive in and park your car</li>
                      <li>Lock the gate behind you</li>
                    </ol>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg mt-3 text-sm">
                    <span>🚗</span> <strong>TRANSPORT:</strong> Please organize your own transport to/from the cruise terminal, unless you have booked our private car transfer service.
                  </div>
                </div>
              ) : (
                /* Marrickville - Directions (no gate code) */
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-[#004280] font-bold text-lg mb-2">
                    <MapPin className="w-5 h-5" />
                    <span>DIRECTIONS</span>
                  </div>
                  <div className="bg-blue-50 border-2 border-[#004280] rounded-lg p-4">
                    <p className="text-gray-700">
                      Upon arriving at 100 Marrickville Road, Marrickville, drive under the rail bridge and keep to the right. 
                      Take the first left through the gates. You will see palm trees and "CP" signage on the fence. 
                      Please park in any available area displaying CP signage.
                    </p>
                  </div>
                </div>
              )}

              {/* Transfer Service Section (if selected) */}
              {bookingDetails.transferService && (
                <div className="mb-4">
                  <div className="bg-teal-600 text-white px-4 py-2 rounded-t-lg font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    OPTIONAL TRANSFER SERVICE
                  </div>
                  <div className="bg-green-50 p-4 border-2 border-teal-200 rounded-b-lg">
                    <p className="font-semibold text-gray-800 mb-2">Private Car Transfer to/from Cruise Terminals</p>
                    <p className="text-gray-700 mb-3">Up to 5 passengers: ${getTransferPrice().toFixed(2)}</p>
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                      <Phone className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-800">
                        <strong>IMPORTANT:</strong> If you have selected car transfers then please contact{' '}
                        <strong className="text-[#004280]">0418 457 219</strong> upon arrival and one of our agents will organise your transfers.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Need Help Contact */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4 flex items-start gap-2">
                <Phone className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-800">
                  <strong>Need Help?</strong> If you have any issues, concerns or difficulty finding the location please call{' '}
                  <strong className="text-[#004280]">0418 457 219</strong>
                </p>
              </div>

              {/* Footer Info */}
              <div className="text-center text-gray-600 text-sm mb-6">
                <p className="font-semibold">Thank you for booking with Cruise Ship Parking Sydney!</p>
                <p>For inquiries: info@cruiseshipparkingsydney.com.au | Phone: 1800 462 445</p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 print:hidden">
                <Button
                  onClick={handlePrint}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  size="lg"
                >
                  <Printer className="mr-2" size={20} />
                  Print Confirmation
                </Button>
                
                <Button
                  onClick={handleDownloadPDF}
                  className="w-full bg-[#00897B] hover:bg-[#00796B] text-white"
                  size="lg"
                >
                  <Download className="mr-2" size={20} />
                  Download PDF Confirmation
                </Button>
                
                <Button
                  onClick={handleBackToHome}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {paymentStatus === 'cancelled' && (
          <Card className="border-none shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">Booking Canceled</h2>
                <p className="text-lg text-red-600 font-semibold mb-4">
                  Due to Non-Payment
                </p>
                <p className="text-gray-600 mb-6">
                  Your payment was not completed. The booking has been canceled and no charges have been made.
                </p>
                
                <Button
                  onClick={handleBackToHome}
                  className="w-full bg-[#00897B] hover:bg-[#00796B] text-white"
                  size="lg"
                >
                  Return to Booking Page
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
